import { Typography, CircularProgress, Grid, Chip } from "@material-ui/core";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import React, { useEffect } from "react";
import { useParams } from "react-router";
import { Wrapper } from "components/wrapper";
import { DetailCard, DetailField, DetailSectionTitle } from "components/detailPage";
import { PhotoGallery } from "components/photoGallery";
import { ExportButtons } from "components/exportButtons";
import { useObraDetailsState } from "./context/obra-details-context";
import { useObraDetailsController } from "./context/obra-details-controller";

const formatCurrency = (value?: number | null) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '-';

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('pt-BR') : '-';

export function ObraDetailsPage() {
  const { obraDetails, loading, error } = useObraDetailsState();
  const { loadObraDetails, clearObraDetails, exportObraDetail } = useObraDetailsController();
  let { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (id) {
      loadObraDetails(parseInt(id));
    }

    return () => {
      clearObraDetails();
    };
  }, [id, loadObraDetails, clearObraDetails]);

  if (loading) {
    return (
      <Wrapper>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </div>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <Typography color="error" variant="h6">{error}</Typography>
      </Wrapper>
    );
  }

  if (!obraDetails) {
    return (
      <Wrapper>
        <Typography variant="h6">Obra não encontrada.</Typography>
      </Wrapper>
    );
  }

  return (
    <>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Obras", href: "/obras" },
          { label: obraDetails.tipoObraName },
        ]}
      />
      <PageTitle>OBRA — {obraDetails.tipoObraName}</PageTitle>
      <Wrapper>
        <ExportButtons onExport={(format) => exportObraDetail(Number(id), format)} />
        <DetailCard>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Chip label={obraDetails.situacaoObraName} color="primary" />
            </Grid>

            <Grid item xs={12} sm={4}>
              <DetailField label="Contrato" value={obraDetails.contratoNumero} />
            </Grid>
            {obraDetails.convenioNumero && (
              <Grid item xs={12} sm={4}>
                <DetailField label="Convênio" value={obraDetails.convenioNumero} />
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <DetailField label="Função" value={obraDetails.funcaoObraName} />
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Cronograma</DetailSectionTitle>
            </Grid>

            <Grid item xs={12} sm={3}>
              <DetailField label="Início" value={formatDate(obraDetails.dtaInicio)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DetailField label="Previsão de Conclusão" value={formatDate(obraDetails.dtaPrevisaoConclusao)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DetailField label="Conclusão" value={formatDate(obraDetails.dtaConclusao)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DetailField
                label="% Executado"
                value={obraDetails.percentualExecutado !== undefined && obraDetails.percentualExecutado !== null ? `${obraDetails.percentualExecutado}%` : undefined}
              />
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Valor</DetailSectionTitle>
            </Grid>

            <Grid item xs={12} sm={6}>
              <DetailField label="Valor da Obra" value={formatCurrency(obraDetails.valorObra)} />
            </Grid>

            {(obraDetails.desMotivoParalisacao || obraDetails.nomResponsavelInexecucao || obraDetails.dtaPrevistaReinicio) && (
              <React.Fragment>
                <Grid item xs={12}>
                  <DetailSectionTitle>Paralisação</DetailSectionTitle>
                </Grid>
                {obraDetails.desMotivoParalisacao && (
                  <Grid item xs={12}>
                    <DetailField label="Motivo da Paralisação" value={obraDetails.desMotivoParalisacao} />
                  </Grid>
                )}
                {obraDetails.nomResponsavelInexecucao && (
                  <Grid item xs={12} sm={6}>
                    <DetailField label="Responsável pela Inexecução" value={obraDetails.nomResponsavelInexecucao} />
                  </Grid>
                )}
                {obraDetails.dtaPrevistaReinicio && (
                  <Grid item xs={12} sm={6}>
                    <DetailField label="Data Prevista de Reinício" value={formatDate(obraDetails.dtaPrevistaReinicio)} />
                  </Grid>
                )}
              </React.Fragment>
            )}

            <Grid item xs={12}>
              <DetailSectionTitle>Itens/Quantitativos Contratados</DetailSectionTitle>
              {obraDetails.itens && obraDetails.itens.length > 0 ? (
                obraDetails.itens.map((item) => (
                  <Typography key={item.id} variant="body2" style={{ marginBottom: 8 }}>
                    {item.desItem} — {item.quantidadeContratada} {item.unidade} x {formatCurrency(item.valorUnitario)}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">Nenhum item contratado informado.</Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Medições</DetailSectionTitle>
              {obraDetails.medicoes && obraDetails.medicoes.length > 0 ? (
                obraDetails.medicoes
                  .slice()
                  .sort((a, b) => new Date(a.dtaMedicao).getTime() - new Date(b.dtaMedicao).getTime())
                  .map((medicao) => (
                    <div key={medicao.id} style={{ marginBottom: 12 }}>
                      <Typography variant="body2">
                        <strong>{formatDate(medicao.dtaMedicao)}</strong> — {formatCurrency(medicao.valorMedicao)}
                        {medicao.percentualExecutado ? ` (${medicao.percentualExecutado}% executado)` : ''}
                      </Typography>
                      {medicao.itens && medicao.itens.length > 0 && (
                        <Typography variant="body2" color="textSecondary">
                          {medicao.itens.map((i) => `${i.itemDesItem}: ${i.quantidadeExecutada} ${i.itemUnidade}`).join(' | ')}
                        </Typography>
                      )}
                    </div>
                  ))
              ) : (
                <Typography variant="body2" color="textSecondary">Nenhuma medição registrada.</Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <DetailSectionTitle>Fotos</DetailSectionTitle>
              <PhotoGallery photos={obraDetails.fotos} />
            </Grid>
          </Grid>
        </DetailCard>
      </Wrapper>
    </>
  );
}
